export function getHistoryStatus(record) {
  if (record.code_released) return "Released";
  if (record.code_requested) return "Requested";
  if (record.order_number || record.order_id) return "Order Attached";
  return "Available";
}

export function getStatusClassName(status) {
  switch (String(status || "").toLowerCase()) {
    case "released":
    case "generation_complete":
      return "released";
    case "requested":
    case "generation_requested":
    case "coupon_generation_queued":
    case "generation_in_process":
      return "requested";
    case "order attached":
    case "generation_error":
      return "attached";
    default:
      return "available";
  }
}

export function getCampaignStatusLabel(status) {
  switch (status) {
    case "generation_requested":
      return "Starting";
    case "coupon_generation_queued":
      return "Scheduled";
    case "generation_in_process":
      return "Preparing codes";
    case "generation_complete":
      return "Codes ready";
    case "generation_error":
      return "Needs review";
    case "draft":
      return "Not started";
    case "active":
      return "Active";
    default:
      return status || "Pending";
  }
}

export function getCampaignProgress(campaign = {}) {
  const requested = Math.max(0, Number(campaign.requested_code_quantity) || 0);
  const stored = Math.max(0, Number(campaign.stored_code_count) || 0);
  const generated = Math.max(0, Number(campaign.generated_code_count) || 0);
  const completedBatches = Array.isArray(campaign.completed_generation_batches)
    ? campaign.completed_generation_batches.length
    : 0;
  const batchSize = Math.max(1, Number(campaign.batch_size) || requested || 1);
  const totalBatches = Math.max(
    0,
    Number(campaign.total_generation_batches) || (requested ? Math.ceil(requested / batchSize) : 0)
  );
  const completedCodes = Math.min(requested, stored);
  const percent = requested ? Math.min(100, Math.round((completedCodes / requested) * 100)) : 0;

  return { requested, stored, generated, completedCodes, completedBatches, totalBatches, percent };
}

export function mergeCampaignMetrics(campaigns = [], metrics = []) {
  const metricsByDesignator = new Map(
    metrics.map((metric) => [String(metric.campaign_designator || "").toUpperCase(), metric])
  );

  return campaigns.map((campaign) => ({
    ...campaign,
    inventory:
      metricsByDesignator.get(String(campaign.campaign_designator || "").toUpperCase()) || {
        total_codes: 0,
        available_codes: 0,
        requested_codes: 0,
        released_codes: 0,
        order_attached_codes: 0,
        blocked_codes: 0,
        assigned_codes: 0,
        redeemed_codes: 0,
        redeemed_release_received_codes: 0,
        successfully_released_order_codes: 0,
        redemption_rate: null,
        true_redemption_rate: null,
        delivered_codes: 0,
        order_redemption_codes: 0,
        order_redemption_rate: null,
        code_released_codes: 0,
        code_released_rate: null,
        unrealized_codes: 0,
        unrealized_rate: null,
        true_redemption_codes: 0,
        delivered_true_redemption_rate: null,
        redemption_metrics_available: false,
        redemption_metrics_version: null,
        updated_at: null,
      },
  }));
}

export function buildGroupedHistory(records) {
  const grouped = records.reduce((collection, record) => {
    const key = record.campaign_designator || "UNASSIGNED";
    if (!collection[key]) {
      collection[key] = [];
    }

    collection[key].push(record);
    return collection;
  }, {});

  return Object.entries(grouped)
    .map(([designator, items]) => {
      const released = items.filter((record) => record.code_released).length;
      const requested = items.filter(
        (record) => record.code_requested && !record.code_released
      ).length;
      const available = items.filter(
        (record) =>
          !record.code_requested && !record.code_released && !record.block_release
      ).length;
      const latestActivity =
        items.reduce(
          (highest, record) =>
            record.activity_timestamp > highest ? record.activity_timestamp : highest,
          0
        ) || null;

      return {
        designator,
        totals: {
          total: items.length,
          released,
          requested,
          available,
          latestActivity,
        },
      };
    })
    .sort(
      (left, right) => (right.totals.latestActivity || 0) - (left.totals.latestActivity || 0)
    );
}

export function buildHistoryMetrics(records) {
  return {
    total: records.length,
    released: records.filter((record) => record.code_released).length,
    requested: records.filter(
      (record) => record.code_requested && !record.code_released
    ).length,
    uniqueDesignators: new Set(
      records.map((record) => record.campaign_designator).filter(Boolean)
    ).size,
    latestActivity:
      records.reduce(
        (highest, record) =>
          record.activity_timestamp > highest ? record.activity_timestamp : highest,
        0
      ) || null,
  };
}

const CAMPAIGN_PREPARING_STATUSES = new Set([
  "generation_requested",
  "coupon_generation_queued",
  "generation_in_process",
]);

const CAMPAIGN_READY_STATUSES = new Set(["generation_complete", "active"]);

export function buildCampaignMetrics(campaigns = []) {
  return campaigns.reduce(
    (metrics, campaign) => {
      const status = String(campaign?.campaign_status || "").toLowerCase();
      metrics.total += 1;
      if (CAMPAIGN_PREPARING_STATUSES.has(status)) metrics.preparing += 1;
      if (CAMPAIGN_READY_STATUSES.has(status)) metrics.ready += 1;
      if (status === "generation_error") metrics.needsAttention += 1;
      return metrics;
    },
    { total: 0, preparing: 0, ready: 0, needsAttention: 0 }
  );
}
